import Constants from 'src/core/utils/Constants';
import type ApiUser from 'src/core/models/api/ApiUser';
import secureLocalStorage from 'react-secure-storage';

const TAG = '[StorageService web] ->';

class StorageService {
	async getAccessToken(): Promise<string | null> {
		return await this.getItem(Constants.storageAccessToken);
	}

	async setAccessToken(accessToken: string): Promise<void> {
		await this.setItem(Constants.storageAccessToken, accessToken);
	}

	async isOnboardingDone(): Promise<boolean | null> {
		const value = this.readItem(Constants.storageOnboardingDone);

		return await Promise.resolve(value === true || value === 'true');
	}

	async setOnboardingDone(isOnboardingDone: boolean): Promise<void> {
		await this.setItem(Constants.storageOnboardingDone, isOnboardingDone);
	}

	async getLanguage(): Promise<string | null> {
		return await this.getItem(Constants.storageLanguage);
	}

	async setLanguage(language: string): Promise<void> {
		await this.setItem(Constants.storageLanguage, language);
	}

	async getUser(): Promise<ApiUser | null> {
		const user = await this.getItem(Constants.storageUser);

		if (!user || user.length === 0) {
			return null;
		}

		return JSON.parse(user) as ApiUser;
	}

	async setUser(user: ApiUser): Promise<void> {
		await this.setItem(Constants.storageUser, JSON.stringify(user));
	}

	async clear(): Promise<void> {
		await this.removeItem(Constants.storageAccessToken);
		await this.removeItem(Constants.storageOnboardingDone);
		await this.removeItem(Constants.storageLanguage);
		await this.removeItem(Constants.storageUser);
	}

	private readItem(key: string): string | number | boolean | object | null {
		try {
			return secureLocalStorage.getItem(key);
		} catch (e) {
			console.warn(TAG, 'getItem() failed', e);
			return null;
		}
	}

	private getItem(key: string): Promise<string | null> {
		const value = this.readItem(key);

		return Promise.resolve(typeof value === 'string' ? value : null);
	}

	private setItem(key: string, value: string | boolean): Promise<void> {
		try {
			secureLocalStorage.setItem(key, value);
		} catch (e) {
			console.warn(TAG, 'setItem() failed', e);
		}

		return Promise.resolve();
	}

	private removeItem(key: string): Promise<void> {
		try {
			secureLocalStorage.removeItem(key);
		} catch (e) {
			console.warn(TAG, 'removeItem() failed', e);
		}

		return Promise.resolve();
	}
}

export default new StorageService();
